/**
 * Money primitives.
 *
 * Every monetary value in LaundryPro — in the database, in the API, in the UI,
 * in invoices, in PDFs and on thermal receipts — is an INTEGER number of minor
 * units (halalas for SAR: 1 SAR = 100 halalas).
 *
 * Floating-point arithmetic is never used for money. IEEE-754 cannot represent
 * 0.1 exactly, so repeated addition and VAT derivation accumulate drift that
 * shows up as invoices whose lines do not sum to their total. Integers do not
 * have that failure mode.
 *
 * `MinorUnits` is a plain `number` rather than a branded type so that values
 * cross the D1 and JSON boundaries without ceremony. The discipline is enforced
 * by `assertMinorUnits`, which every write path calls, rather than by the type
 * system alone.
 */

/** An integer count of minor units. 15000 means 150.00 SAR. */
export type MinorUnits = number;

/** A rate in basis points: 1500 = 15.00%. Integer, so the rate never drifts. */
export type BasisPoints = number;

export const MINOR_UNITS_PER_MAJOR = 100;
export const BASIS_POINTS_DENOMINATOR = 10_000;

/**
 * The largest magnitude accepted for a monetary value.
 *
 * `Number.MAX_SAFE_INTEGER` is the point where integer arithmetic silently
 * stops being exact. Rejecting well below it leaves room for intermediate
 * products (`unitPrice * quantity * rate`) to stay exact too. 10^13 minor units
 * is 100 billion SAR — far beyond any real invoice, and small enough that
 * `value * BASIS_POINTS_DENOMINATOR` stays under MAX_SAFE_INTEGER.
 */
export const MAX_MINOR_UNITS: MinorUnits = 10_000_000_000_000;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyError";
  }
}

/**
 * Validates a value is usable as money. Call this at every boundary where a
 * number enters the system: API request bodies, database reads, and imports.
 */
export function assertMinorUnits(value: unknown, label = "amount"): asserts value is MinorUnits {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new MoneyError(`${label} must be a finite number, received ${String(value)}`);
  }
  if (!Number.isInteger(value)) {
    throw new MoneyError(`${label} must be an integer number of minor units, received ${value}`);
  }
  if (Math.abs(value) > MAX_MINOR_UNITS) {
    throw new MoneyError(`${label} exceeds the maximum supported magnitude (${MAX_MINOR_UNITS})`);
  }
}

export function isMinorUnits(value: unknown): value is MinorUnits {
  try {
    assertMinorUnits(value);
    return true;
  } catch {
    return false;
  }
}

export function assertNonNegative(value: MinorUnits, label = "amount"): void {
  assertMinorUnits(value, label);
  if (value < 0) {
    throw new MoneyError(`${label} must not be negative, received ${value}`);
  }
}

export function assertBasisPoints(value: unknown, label = "rate"): asserts value is BasisPoints {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new MoneyError(`${label} must be an integer number of basis points, received ${String(value)}`);
  }
  if (value < 0 || value > BASIS_POINTS_DENOMINATOR) {
    throw new MoneyError(`${label} must be between 0 and ${BASIS_POINTS_DENOMINATOR} basis points, received ${value}`);
  }
}

/**
 * Rounds to the nearest whole minor unit, half away from zero.
 *
 * `Math.round` alone rounds half UP, which is asymmetric for negatives
 * (`Math.round(-0.5)` is `-0`, not `-1`). Credit notes and refunds carry
 * negative amounts, so rounding must mirror across zero or a refund would not
 * exactly reverse the charge it reverses.
 */
export function roundMinorUnits(value: number): MinorUnits {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`Cannot round a non-finite value: ${value}`);
  }
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * Converts a major-unit amount (150.5 SAR) to minor units (15050).
 *
 * ⚠️ LOSSY AT THE BOUNDARY — do not use on API input.
 *
 * The loss happens before this function runs. `1.005` cannot be represented in
 * IEEE-754; the nearest double is 1.00499999999999989…, so this returns 100 and
 * not the 101 a decimal system would give. No implementation can recover the
 * digits a human typed once they have been parsed into a float.
 *
 * Therefore: the API accepts money as INTEGER MINOR UNITS, never as a decimal
 * number. When an amount arrives as human-entered text, use
 * {@link parseMajorToMinor}, which reads the digits and is exact. This function
 * is for seed data, fixtures and tests, where the input is a literal the author
 * chose.
 */
export function majorToMinor(major: number): MinorUnits {
  if (typeof major !== "number" || !Number.isFinite(major)) {
    throw new MoneyError(`Cannot convert a non-finite value to minor units: ${String(major)}`);
  }
  const minor = roundMinorUnits(major * MINOR_UNITS_PER_MAJOR);
  assertMinorUnits(minor);
  return minor;
}

/**
 * Parses a decimal amount written as text ("1.005", "150.50", "-3.26") into
 * minor units, exactly.
 *
 * Works on the digit string rather than on a float, so it is not subject to the
 * representation error described on {@link majorToMinor}: "1.005" yields 101,
 * because the third decimal digit is read directly and rounded half away from
 * zero.
 *
 * This is the correct converter for anything a human typed.
 */
export function parseMajorToMinor(input: string): MinorUnits {
  if (typeof input !== "string") {
    throw new MoneyError(`Expected a decimal string, received ${typeof input}`);
  }
  const text = input.trim().replace(/[,_\s]/g, "");
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) {
    throw new MoneyError(`"${input}" is not a valid decimal amount`);
  }

  const sign = match[1] === "-" ? -1 : 1;
  const whole = match[2] ?? "0";
  const fraction = match[3] ?? "";

  // Guard before arithmetic: a 30-digit whole part would overflow silently.
  if (whole.length > 15) {
    throw new MoneyError(`"${input}" exceeds the maximum supported magnitude`);
  }

  const centis = (fraction + "00").slice(0, 2);
  let minor = Number(whole) * MINOR_UNITS_PER_MAJOR + Number(centis);

  // Round half away from zero using the digit that was cut off, so the result
  // matches decimal arithmetic rather than binary approximation.
  const nextDigit = fraction.length > 2 ? Number(fraction[2]) : 0;
  if (nextDigit >= 5) {
    minor += 1;
  }

  const signed = sign * minor;
  assertMinorUnits(signed);
  return signed;
}

/**
 * Converts minor units to a major-unit number.
 *
 * For DISPLAY and for interoperating with systems that require decimals only.
 * Never feed the result back into a calculation — use `formatMinorUnits` for
 * anything user-facing.
 */
export function minorToMajor(minor: MinorUnits): number {
  assertMinorUnits(minor);
  return minor / MINOR_UNITS_PER_MAJOR;
}

/**
 * Formats minor units as a fixed 2-decimal string: 15050 → "150.50".
 *
 * Built by integer division rather than `toFixed`, so no float ever touches the
 * value. This exact representation is what the ZATCA QR payload and invoice
 * documents consume.
 */
export function formatMinorUnits(minor: MinorUnits): string {
  assertMinorUnits(minor);
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const major = Math.trunc(abs / MINOR_UNITS_PER_MAJOR);
  const remainder = abs % MINOR_UNITS_PER_MAJOR;
  return `${negative ? "-" : ""}${major}.${String(remainder).padStart(2, "0")}`;
}

/** Sums minor-unit amounts exactly, validating every term. */
export function sumMinorUnits(values: readonly MinorUnits[]): MinorUnits {
  let total = 0;
  for (const value of values) {
    assertMinorUnits(value);
    total += value;
  }
  assertMinorUnits(total, "sum");
  return total;
}

/**
 * Multiplies an amount by a quantity.
 *
 * Quantity may be fractional (2.5 kg of laundry), so the product is rounded
 * back to a whole minor unit exactly once, here — never left as a float for a
 * caller to round again later.
 */
export function multiplyMinorUnits(amount: MinorUnits, quantity: number): MinorUnits {
  assertMinorUnits(amount);
  if (typeof quantity !== "number" || !Number.isFinite(quantity) || quantity < 0) {
    throw new MoneyError(`Quantity must be a finite non-negative number, received ${String(quantity)}`);
  }
  const product = roundMinorUnits(amount * quantity);
  assertMinorUnits(product, "product");
  return product;
}

/** Applies a basis-point rate to an amount, rounding once. */
export function applyBasisPoints(amount: MinorUnits, rateBps: BasisPoints): MinorUnits {
  assertMinorUnits(amount);
  assertBasisPoints(rateBps);
  return roundMinorUnits((amount * rateBps) / BASIS_POINTS_DENOMINATOR);
}
